import { Modal, Form, Input, Button, message, Upload } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import drEasyApi from "../../Api/Customapi";
import { useGetUserData } from "../../hooks/useGetUserData";
import { getImg } from "../../Api/getImg";
import { UploadOutlined } from "@ant-design/icons";
import getErrorMessage from "../../hooks/getErrorMssg";
import { useIsMobile } from "../../hooks/use-mobile";
import ImageCropModal from "../Modals/ImageCropModal";

const EditSpecProfileModal = ({ open, onClose }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const [photoIdPreview, setPhotoIdPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedImageSrc, setSelectedImageSrc] = useState(null);
    const [aboutCharCount, setAboutCharCount] = useState(0);
    const isMobile = useIsMobile();

    const { data: userData, isLoading } = useGetUserData();
console.log(userData);

    useEffect(() => {
        if (userData) {
            form.setFieldsValue({
                firstName: userData.firstName,
                lastName: userData.lastName,
                about: userData.about,
                photoId: userData.imageId,
                email: userData.email,
                phoneNumber: userData.phoneNumber,
            });
            setPhotoIdPreview(userData.imageId);
            // Set initial character count for about field
            setAboutCharCount(userData.about ? userData.about.length : 0);
        }
    }, [userData, form]);

    const { mutate, isPending } = useMutation({
        mutationFn: async (values) => {
            const payload = { 
                ...values,
                slogan: values.slogan && values.slogan.trim() !== '' ? values.slogan.trim() : ""
            };
            if (!values.newPassword) {
                delete payload.oldPassword;
                delete payload.newPassword;
            }
            await drEasyApi.put("/api/Doctor/editProfile", payload);
        },
        onSuccess: () => {
            message.success("Profile updated successfully");
            queryClient.invalidateQueries(["userData"]);
            form.setFieldsValue({ oldPassword: undefined, newPassword: undefined });
            onClose();
        },
        onError: (error) => {
            message.error(getErrorMessage(error));
        },
    });

    const handleFinish = (values) => {
        if (values.newPassword && !values.oldPassword) {
            message.warning("Old password is required to set a new password");
            return;
        }
        mutate(values);
    };

    const handleAboutChange = (e) => {
        const value = e.target.value;
        setAboutCharCount(value.length);
        form.setFieldsValue({ about: value });
    };

    const handleFileSelect = (file) => {
        const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
        if (!isValidType) {
            message.error('You can only upload JPG/JPEG/PNG files!');
            return false;
        }

        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('Image must be smaller than 10MB!');
            return false;
        }

        const reader = new FileReader();
        reader.addEventListener('load', () => {
            setSelectedImageSrc(reader.result);
            setSelectedImageFile(file);
            setCropModalOpen(true);
        });
        reader.readAsDataURL(file);
        
        return false; // Prevent auto upload
    };

    const handleCropComplete = async (croppedImageBlob) => {
        setUploading(true);
        setCropModalOpen(false);
        
        const formData = new FormData();
        
        const originalExtension = selectedImageFile.name.split('.').pop().toLowerCase();
        const croppedFileName = `cropped.${originalExtension}`;
        
        const croppedFile = new File([croppedImageBlob], croppedFileName, {
            type: selectedImageFile.type, 
            lastModified: Date.now(),
        });
        formData.append("file", croppedFile);

        try {
            const response = await drEasyApi.post(
                "/api/Storage/UploadImage",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            const imageId = response.data.id;
            form.setFieldsValue({ photoId: imageId });
            setPhotoIdPreview(imageId);
            message.success("Image uploaded successfully");
        } catch (error) {
            console.error("Image upload failed", error);
            message.error("Image upload failed");
        } finally {
            setUploading(false);
            setSelectedImageFile(null);
            setSelectedImageSrc(null);
        }
    };

    const handleCropCancel = () => {
        setCropModalOpen(false);
        setSelectedImageFile(null);
        setSelectedImageSrc(null);
    };

    return (
        <>
            <Modal
                title={<span className="text-lg sm:text-xl font-semibold">Edit Profile</span>}
                centered
                open={open}
                onCancel={onClose}
                footer={null}
                destroyOnClose
                width={isMobile ? '95%' : 600}
                className="edit-profile-modal"
                styles={{
                    body: { 
                        padding: isMobile ? '16px' : '24px',
                        maxHeight: isMobile ? '80vh' : 'none',
                        overflowY: isMobile ? 'auto' : 'visible'
                    }
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    initialValues={{ remember: true }}
                    size={isMobile ? 'middle' : 'large'}
                >

                    {/* Name fields */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
                        <Form.Item 
                            name="firstName" 
                            label={<span className="text-sm sm:text-base font-medium">First Name</span>}
                            className="flex-1 mb-4"
                            rules={[{ required: true, message: 'Please enter your first name' }]}
                        >
                            <Input 
                                placeholder="Enter first name"
                                className="h-10 sm:h-12"
                            />
                        </Form.Item>

                        <Form.Item 
                            name="lastName" 
                            label={<span className="text-sm sm:text-base font-medium">Last Name</span>}
                            className="flex-1 mb-4"
                            rules={[{ required: true, message: 'Please enter your last name' }]}
                        >
                            <Input 
                                placeholder="Enter last name"
                                className="h-10 sm:h-12"
                            />
                        </Form.Item>
                    </div>
                  
                    {/* Name fields */}
                  
                    {/* Email */}
                    <Form.Item 
                        name="email" 
                        label={<span className="text-sm sm:text-base font-medium">Email</span>}
                        className="mb-4"
                        rules={[
                            { required: true, message: 'Please enter your email' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input 
                            type="email" 
                            placeholder="Enter email address"
                            className="h-10 sm:h-12"
                        />
                    </Form.Item>

                    {/* Phone Number */}
                    <Form.Item 
                        name="phoneNumber" 
                        label={<span className="text-sm sm:text-base font-medium">Phone Number</span>}
                        className="mb-4"
                    >
                        <Input 
                            placeholder="Enter phone number"
                            className="h-10 sm:h-12"
                        />
                    </Form.Item>

                  
                    {/* About */}
                    <Form.Item 
                        name="about" 
                        label={<span className="text-sm sm:text-base font-medium">About</span>}
                        className="mb-0"
                        rules={[
                            { min: 10, message: 'About must be at least 10 characters long' },
                            { max: 500, message: 'About cannot exceed 500 characters' }
                        ]}
                    >
                        <Input.TextArea  
                            maxLength={500}
                            onChange={handleAboutChange}
                            rows={isMobile ? 3 : 4} 
                            placeholder="Tell us about yourself..."
                            className="resize-none"
                        />
                    </Form.Item>  
             
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs text-gray-500">
                            min: 10 characters, max: 500 characters
                        </span>
                        <span className="text-xs text-gray-600 font-medium">
                            {aboutCharCount}/500
                        </span>
                    </div>

                    {/* Profile Photo */}
                    <Form.Item 
                        label={<span className="text-sm sm:text-base font-medium">Profile Photo</span>}
                        className="my-4"
                    >
                        <div className="space-y-3">
                            <Upload
                                name="file"
                                accept=".jpg,.jpeg,.png"  
                                showUploadList={false}
                                beforeUpload={handleFileSelect}
                                customRequest={() => {}} // Empty function since we handle upload after crop
                            >
                                <Button 
                                    icon={<UploadOutlined />} 
                                    loading={uploading}
                                    className={`${isMobile ? 'h-10' : 'h-12'} flex items-center`}
                                    size={isMobile ? 'middle' : 'large'}
                                >
                                    {uploading ? 'Uploading...' : 'Upload & Crop Photo'}
                                </Button>
                            </Upload>

                            {photoIdPreview && (
                                <div className="flex justify-center sm:justify-start">
                                    <img
                                        src={getImg(photoIdPreview)}
                                        alt="Profile Preview"
                                        className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-lg object-fit  border-2 border-gray-200 shadow-sm"
                                    />
                                </div>
                            )}
                        </div>
                    </Form.Item>

                    <Form.Item name="photoId" hidden>
                        <Input />
                    </Form.Item>

                    {/* Password Section */}
                    <div className="space-y-4 mb-6">
                        <h3 className="text-base sm:text-lg font-medium text-gray-800 border-b pb-2">
                            Change Password (Optional)
                        </h3>
                        
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
                            <Form.Item 
                                name="oldPassword" 
                                label={<span className="text-sm sm:text-base font-medium">Current Password</span>}
                                className="flex-1 mb-2 sm:mb-4"
                            >
                                <Input.Password 
                                    placeholder="Enter current password"
                                    className="h-10 sm:h-12"
                                />
                            </Form.Item>

                            <Form.Item 
                                name="newPassword" 
                                label={<span className="text-sm sm:text-base font-medium">New Password</span>}
                                className="flex-1 mb-2 sm:mb-4"
                            >
                                <Input.Password 
                                    placeholder="Enter new password"
                                    className="h-10 sm:h-12"
                                />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <Form.Item className="mb-0">
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                            <Button 
                                onClick={onClose}
                                size={isMobile ? 'middle' : 'large'}
                                className={`w-full sm:w-auto ${isMobile ? 'h-10' : 'h-12'} flex items-center justify-center`}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="text" 
                                htmlType="submit" 
                                loading={isPending} 
                                disabled={uploading}
                                size={isMobile ? 'middle' : 'large'}
                                className={`w-full sm:w-auto ${isMobile ? 'h-10' : 'h-12'} flex items-center justify-center font-medium bg-primary text-white border border-transparent hover:bg-primary_hover`}
                            >
                                {isPending ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            <ImageCropModal
                open={cropModalOpen}
                imageSrc={selectedImageSrc}
                fileName={selectedImageFile?.name} 
                onCropComplete={handleCropComplete}
                onCancel={handleCropCancel}
                isMobile={isMobile}
            />
        </>
    );
};

export default EditSpecProfileModal;